import { useEffect, useRef, useState } from 'react';

import type { CatalogueDesign } from '@/api/masters';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { DesignPhoto } from '@/components/DesignPhoto';
import { Button, Card, Notice } from '@/components/PageHeading';
import { useOpenAtTop } from '@/components/useOpenAtTop';
import { formatAmount } from '@core/format/money';
import { bagOf } from '@core/bookings/bag';
import { garmentSheet, moveGarment, swapGarment } from '@core/bookings/garmentSheet';
import { nextStep, problemsFor, type NewBookingStep } from '@core/bookings/newBookingSteps';
import { replaceTaken, takenMeanwhile } from '@core/bookings/takenMeanwhile';
import { chargeableDays } from '@core/bookings/pricing';
import { type AvailableUnit } from '@core/bookings/rail';
import { garmentFromUnit, repriceDraft } from '@core/bookings/takeBooking';
import {
  addGarment,
  addWearer,
  choosePayer,
  claimedUnitIds,
  discountGarment,
  draftMoney,
  draftProblems,
  emptyDraft,
  forTheCustomer,
  payerKey,
  productBookingDefaults,
  removeGarment,
  setGarmentAccessories,
  wearerLabel,
  type BookingDraft,
  type DraftGarment,
} from '@core/bookings/draft';
import type { BookingDefaults } from '@core/session/types';

import { CustomerDrawer } from '../customers/CustomerDrawer';
import { DesignRail } from './DesignRail';
import { GarmentAccessoriesModal } from './GarmentAccessoriesModal';
import { GarmentDiscountModal } from './GarmentDiscountModal';
import { GarmentSheet } from './GarmentSheet';
import { BagStep } from './newBooking/BagStep';
import { GarmentSearch } from './newBooking/GarmentSearch';
import { HoldQuoteDialog } from './newBooking/HoldQuoteDialog';
import { StepBand, WhoWears } from './newBooking/StepBand';
import { WhenStep } from './newBooking/WhenStep';

// The rail's vocabulary lives in `rail.ts` with the functions over it, and is re-exported here
// because every caller reaches for it through this screen.
export type { AvailableUnit, HeldGarment } from '@core/bookings/rail';
import { withReference } from '@core/units/referenceNumber';

/**
 * Taking a booking on the website **the way the phone does** (owner, 2026-10-22; the canvas's "Booking
 * on the web" page): three steps in the order a customer asks — **when, then what, then who**.
 *
 * <ol>
 * <li><strong>When</strong>: the event day on a month; collect, return and charged days follow.</li>
 * <li><strong>The looks</strong>: who the next look is for, then the design cards for those dates,
 * with the bag beside them.</li>
 * <li><strong>Bag and confirm</strong>: the customer picked at checkout, the money as lines with
 * Change, and one button that confirms and takes the token.</li>
 * </ol>
 *
 * <p>
 * <strong>The model underneath is unchanged and shared.</strong> `@core/bookings/draft` is the whole
 * state, `draftProblems` decides whether it can be taken, and the steps and where each problem is
 * fixed are `@core/bookings/newBookingSteps` — the phone's own — so the two cannot take a booking
 * differently. It replaced a single page of always-open sections; the "nothing folds" rule that page
 * kept was retired with it.
 * </p>
 */
export function NewBookingPage({
  today,
  branchId,
  units,
  designs = [],
  moreDesigns,
  searching = false,
  searched = false,
  onSearch,
  onClose,
  onCreate,
  onChaseHold,
  busy = false,
  currency = '',
  discountCeilingPct = 0,
  bookingDefaults = productBookingDefaults,
}: {
  today: string;
  branchId: number | undefined;
  /** Results for the current dates. The caller owns the query. */
  units: AvailableUnit[];
  /** The same answer by design — what the design-card rail draws. */
  designs?: CatalogueDesign[];
  /** Said under the design cards when the catalogue holds more than one answer carries. */
  moreDesigns?: string | null | undefined;
  searching?: boolean;
  /** Whether a search has been run at all, so "none free" reads differently from "not asked yet". */
  searched?: boolean;
  onSearch: (input: { from: string; to: string }) => void;
  onClose: () => void;
  /** Takes the booking as the draft says — confirmed, or held, quoted or noted. */
  onCreate: (draft: BookingDraft) => void;
  /** Somebody else holds this garment and the clerk wants to chase it — the parent's drawer. */
  onChaseHold?: ((unit: AvailableUnit) => void) | undefined;
  busy?: boolean;
  currency?: string;
  /**
   * The largest discount this clerk may give **at the acting branch** — never the user-level figure,
   * which reads zero for a manager whose authority is per shop. Zero hides the discount entirely.
   * Advisory: the server resolves the ceiling again.
   */
  discountCeilingPct?: number;
  /** The shop's own deposit multiple and timing, and the grace either side of the event. */
  bookingDefaults?: BookingDefaults;
}) {
  // No event day yet: the first step's one question is the day, so nothing is assumed for it.
  const [stored, setDraft] = useState<BookingDraft>(() => emptyDraft('', bookingDefaults));
  const [step, setStep] = useState<NewBookingStep>('when');
  // Who the next look goes to — asked first, above the looks. "The customer" until someone else.
  const [forKey, setForKey] = useState<string>(payerKey);
  const [creatingCustomer, setCreatingCustomer] = useState<string | null>(null);
  const [holdOrQuote, setHoldOrQuote] = useState(false);
  const [discountingGarment, setDiscountingGarment] = useState<DraftGarment | null>(null);
  const [accessorising, setAccessorising] = useState<DraftGarment | null>(null);
  const [sheetUnit, setSheetUnit] = useState<number | null>(null);
  // A garment being swapped: while set, the rail's next pick takes its place instead of adding.
  const [replacing, setReplacing] = useState<number | null>(null);
  const [openDesign, setOpenDesign] = useState<number | null>(null);
  // What Any has typed in the search — the design cards narrow to it — and the short name chosen.
  const [railQuery, setRailQuery] = useState('');
  const [searchCode, setSearchCode] = useState<string | null>(null);
  const top = useRef<HTMLDivElement>(null);

  const days = chargeableDays(stored.chargeFrom, stored.chargeTo);

  // Derived every render rather than synced by an effect: every line priced for the charged days
  // from its own card, and the ceiling of the branch the clerk is acting at now — which arrives after
  // `/api/auth/me` and narrows when the branch is switched mid-draft.
  const draft = repriceDraft({ ...stored, discountCeilingPct }, days);
  const money = draftMoney(draft);
  const problems = draftProblems(draft);
  const bag = bagOf(draft);
  const claimed = claimedUnitIds(draft);
  const taken = takenMeanwhile(draft, designs);

  const { possessionFrom, possessionTo } = draft;

  /*
    The rail searches itself whenever the possession dates say something askable — ordered and
    complete only, so a half-moved range does not flash "nothing free". `onSearch` is in the list
    because the parent wraps it in `useCallback`.
  */
  useEffect(() => {
    if (!possessionFrom || !possessionTo || possessionFrom > possessionTo) {
      return;
    }

    onSearch({ from: possessionFrom, to: possessionTo });
  }, [possessionFrom, possessionTo, onSearch]);

  // Each step opens at its top.
  useOpenAtTop();
  const goTo = (next: NewBookingStep | null) => {
    if (next) {
      setStep(next);
      top.current?.scrollIntoView({ block: 'start' });
    }
  };

  const fields = useAutoFocusFirstField<HTMLDivElement>();

  // "The customer" is always offered, even before anything is in the bag; then everyone named since.
  const party = forTheCustomer(draft).wearers;
  const target = party.find((wearer) => wearer.key === forKey) ?? party[0]!;
  const swapping = replacing === null ? null : garmentSheet(draft, replacing, currency);
  const sheet = sheetUnit === null ? null : garmentSheet(draft, sheetUnit, currency);
  const forName = swapping ? swapping.wearerName : wearerLabel(target);

  // The chosen person's garments — their sizes read as theirs on the cards.
  const mine = new Set(target.garments.map((garment) => garment.unitId));
  const ownerOf = (unitId: number) => {
    const wearer = draft.wearers.find((each) => each.garments.some((g) => g.unitId === unitId));

    return wearer ? wearerLabel(wearer) : null;
  };

  const garmentOf = (unitId: number) =>
    draft.wearers.flatMap((wearer) => wearer.garments).find((each) => each.unitId === unitId) ??
    null;

  /** A picked garment: in place of the one being swapped, or for whoever the next look is for. */
  const place = (garment: DraftGarment) => {
    if (swapping) {
      setDraft(swapGarment(draft, swapping.unitId, garment));
      setReplacing(null);
      return;
    }

    const withThem = target.key === payerKey ? forTheCustomer(draft) : draft;
    setDraft(addGarment(withThem, target.key, garment));
  };

  /** Somebody new in the party, chosen at once so the next look goes to them. */
  const addPerson = (name: string, relation: string) => {
    const next = addWearer(draft, { name, relation, mobile: '', customerId: null, garments: [] });

    setDraft(next);
    setForKey(next.wearers.at(-1)?.key ?? payerKey);
  };

  /** From the garment's panel: swap it — for another size of its design, or any design. */
  const startSwap = (unitId: number, itemId: number | null) => {
    setSheetUnit(null);
    setReplacing(unitId);
    setOpenDesign(itemId);
    goTo('looks');
  };

  const take = (taking: BookingDraft) => onCreate(taking);

  return (
    <>
      <div ref={top} className="scroll-mt-4">
        <StepBand step={step} draft={draft} onStep={goTo} onClose={onClose} />
      </div>

      <div ref={fields} {...enterKeyScope}>
        {step === 'when' && (
          <WhenStep
            draft={draft}
            today={today}
            // Before a day is chosen, "the event date anchors every other date" is the screen's own
            // question, not a problem to report.
            problems={draft.eventDate ? problemsFor('when', problems) : []}
            busy={busy}
            onChange={setDraft}
            onNext={() => goTo(nextStep('when'))}
          />
        )}

        {step === 'looks' && (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
              <Card>
                <WhoWears
                  people={party.map((wearer) => ({
                    key: wearer.key,
                    label: wearerLabel(wearer),
                    relation: wearer.relation,
                    garments: wearer.garments.length,
                  }))}
                  forKey={target.key}
                  busy={busy}
                  onForChange={setForKey}
                  onAddPerson={addPerson}
                />
              </Card>
              {bag.garments > 0 &&
                problemsFor('looks', problems).map((problem) => (
                  <Notice key={problem.message} tone="warning" title={problem.message} />
                ))}
              <Card>
                {swapping && (
                  <div className="mb-3">
                    <Notice tone="warning" title={`Swapping ${swapping.serialCode}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span>The garment picked next takes its place, for the same person.</span>
                        <Button onClick={() => setReplacing(null)}>Keep it</Button>
                      </div>
                    </Notice>
                  </div>
                )}
                <GarmentSearch
                  designs={designs}
                  units={units}
                  claimed={claimed}
                  ownerOf={ownerOf}
                  forName={forName}
                  disabled={busy}
                  query={railQuery}
                  onQuery={setRailQuery}
                  onDesignChange={setSearchCode}
                  onChaseHold={onChaseHold}
                  onAdd={(unit) => place(garmentFromUnit(unit, days))}
                />
              </Card>
              {searchCode === null && (
                <DesignRail
                  designs={designs}
                  units={units}
                  days={days}
                  currency={currency}
                  claimed={claimed}
                  forName={forName}
                  searching={searching}
                  searched={searched}
                  disabled={busy}
                  more={moreDesigns}
                  query={railQuery}
                  mine={mine}
                  onRemove={(unitId) => setDraft(removeGarment(draft, unitId))}
                  replacing={
                    swapping
                      ? { serialCode: swapping.serialCode, onCancel: () => setReplacing(null) }
                      : null
                  }
                  openId={openDesign}
                  onOpenChange={setOpenDesign}
                  onAdd={(unit) => place(garmentFromUnit(unit, days))}
                />
              )}
            </div>

            {/* The bag beside the looks, grouped by person — the phone's bar at the foot. */}
            <div className="col-span-12 xl:col-span-4">
              <div className="sticky top-4">
                <Card>
                  <div className="mb-3 flex items-baseline justify-between gap-2">
                    <p className="mb-0 text-[15px] font-bold text-gray-900">Bag</p>
                    <span className="text-default text-[12px]">{bag.headline}</span>
                  </div>

                  <div className="flex flex-col gap-3">
                    {party.map((wearer) => {
                      const person = bag.people.find((each) => each.key === wearer.key);
                      const lines = person?.lines ?? [];

                      return (
                        <div key={wearer.key} className="flex flex-col gap-1.5">
                          <p className="text-default mb-0 text-[11.5px] font-bold tracking-wider uppercase">
                            {wearerLabel(wearer)}
                            {wearer.relation ? ` · ${wearer.relation}` : ''}
                          </p>
                          {lines.length === 0 ? (
                            <p className="bg-light text-default mb-0 rounded-lg px-3 py-2.5 text-[13px]">
                              Nothing yet — choose {wearerLabel(wearer)} above, then a garment.
                            </p>
                          ) : (
                            lines.map((line) => (
                              <button
                                key={line.unitId}
                                type="button"
                                onClick={() => setSheetUnit(line.unitId)}
                                className="border-border-color flex w-full cursor-pointer items-center gap-2.5 rounded-[10px] border bg-white px-2.5 py-2 text-start hover:bg-light"
                              >
                                <span className="w-10 shrink-0 overflow-hidden rounded-lg">
                                  <DesignPhoto
                                    url={line.imageUrl}
                                    alt=""
                                    categoryPath={line.categoryName}
                                  />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-[13px] font-semibold text-gray-900">
                                    {line.name}
                                  </span>
                                  <span className="text-default font-mono-nums block truncate text-[12px]">
                                    {withReference(line.serialCode, line.referenceNumber)}
                                  </span>
                                </span>
                                <span className="font-mono-nums text-[13px] font-semibold text-gray-900">
                                  {line.charged === null
                                    ? '—'
                                    : `${currency}${formatAmount(line.charged)}`}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-border-color mt-3 flex items-baseline justify-between border-t pt-3">
                    <span className="text-[14px] font-semibold text-gray-900">
                      Rent · {days} {days === 1 ? 'day' : 'days'}
                    </span>
                    <span className="text-primary font-mono-nums font-serif text-2xl font-semibold">
                      {currency}
                      {formatAmount(money.netRental)}
                    </span>
                  </div>
                  <p className="text-default mt-1 mb-0 text-[12px]">
                    Before discount and deposit — both are on the next step. Click a garment to swap
                    it, add accessories or take it out.
                  </p>

                  <Button
                    variant="primary"
                    data-enter-key-commit
                    disabled={bag.garments === 0}
                    onClick={() => goTo(nextStep('looks'))}
                    className="mt-3 h-11 w-full justify-center text-[14px]"
                  >
                    Bag and confirm
                    <i className="icon icon-arrow-right" />
                  </Button>
                </Card>
              </div>
            </div>
          </div>
        )}

        {step === 'bag' && (
          <BagStep
            draft={draft}
            branchId={branchId}
            problems={problems}
            taken={taken}
            currency={currency}
            shopMultiplier={bookingDefaults.depositMultiplier}
            busy={busy}
            onChange={setDraft}
            onAddMore={() => goTo('looks')}
            onOpenGarment={setSheetUnit}
            onReplaceTaken={(only) => setDraft(replaceTaken(draft, only ? [only] : taken, days))}
            onCreateCustomer={setCreatingCustomer}
            onConfirm={() => take({ ...draft, startAs: 'Confirmed' })}
            onInstead={() => setHoldOrQuote(true)}
          />
        )}
      </div>

      {holdOrQuote && (
        <HoldQuoteDialog
          draft={draft}
          busy={busy}
          onTake={(taking) => {
            setHoldOrQuote(false);
            take(taking);
          }}
          onClose={() => setHoldOrQuote(false)}
        />
      )}

      {creatingCustomer !== null && (
        <CustomerDrawer
          editing={{ mode: 'create' }}
          prefillMobile={/^\d[\d\s+-]*$/.test(creatingCustomer) ? creatingCustomer : ''}
          prefillName={/^\d[\d\s+-]*$/.test(creatingCustomer) ? '' : creatingCustomer}
          onCreated={(customer) => {
            setDraft(choosePayer(draft, { id: customer.id, name: customer.name }));
            setCreatingCustomer(null);
          }}
          onClose={() => setCreatingCustomer(null)}
        />
      )}

      {sheet && (
        <GarmentSheet
          sheet={sheet}
          currency={currency}
          busy={busy}
          onClose={() => setSheetUnit(null)}
          onDiscount={() => {
            setDiscountingGarment(garmentOf(sheet.unitId));
            setSheetUnit(null);
          }}
          onAccessories={() => {
            setAccessorising(garmentOf(sheet.unitId));
            setSheetUnit(null);
          }}
          onChangeSize={() => startSwap(sheet.unitId, sheet.itemId)}
          onSwapDesign={() => startSwap(sheet.unitId, null)}
          onMove={(wearerKey) => {
            setDraft(moveGarment(draft, sheet.unitId, wearerKey));
            setSheetUnit(null);
          }}
          onRemove={() => {
            setDraft(removeGarment(draft, sheet.unitId));
            setSheetUnit(null);
          }}
        />
      )}

      {discountingGarment && (
        <GarmentDiscountModal
          name={discountingGarment.itemName}
          serialCode={discountingGarment.serialCode}
          rate={discountingGarment.rate ?? 0}
          initial={{
            pct: discountingGarment.discountPct,
            amount: discountingGarment.discountAmount,
          }}
          ceilingPct={draft.discountCeilingPct}
          currency={currency}
          busy={busy}
          onClose={() => setDiscountingGarment(null)}
          onApply={(discount) => {
            setDraft(discountGarment(draft, discountingGarment.unitId, discount));
            setDiscountingGarment(null);
          }}
        />
      )}

      {accessorising && (
        <GarmentAccessoriesModal
          name={accessorising.itemName}
          serialCode={accessorising.serialCode}
          initial={accessorising.accessories}
          currency={currency}
          busy={busy}
          onClose={() => setAccessorising(null)}
          onApply={(accessories) => {
            setDraft(setGarmentAccessories(draft, accessorising.unitId, accessories));
            setAccessorising(null);
          }}
        />
      )}
    </>
  );
}
